import { useEffect, useRef, useState } from "react";
import {
  canContinueReadiness,
  type DeviceOption,
  type MediaDevicePreferences
} from "../../services/mediaDevicePreferences";
import { evaluateMediaSupport, MediaUnavailableError, mediaSupportCode } from "../../services/mediaSupport";
import { useI18n } from "../../i18n/localization";
import { MaterialMediaIcon } from "./MaterialMediaIcon";
import { PreviewTrackEndedError, ReadinessSession, type PreviewHealth } from "./readinessSession";

// MiroTalk-style "get ready" screen shown BEFORE the traffic waiting station / field officer
// directory (design/05). Live camera preview + camera/mic toggles + device selectors, then a
// Hebrew action button. On continue the chosen devices + initial on/off state are handed back
// (MediaDevicePreferences) and carried into the call. Preview tracks are always stopped on
// unmount / continue so the camera light never leaks. The preview lifecycle lives in
// ReadinessSession (unit-tested); `autoContinue` (guest invitation flow only) replaces the button
// with a status line and continues once — when both camera and microphone tracks are working.
function toReadinessError(e: unknown, noDevice: string, trackEnded: string, messageFor: (code: string) => string): string {
  if (e instanceof PreviewTrackEndedError) return trackEnded;
  if (e instanceof MediaUnavailableError) return messageFor(mediaSupportCode(e.support));
  if (e instanceof DOMException) {
    if (e.name === "NotAllowedError" || e.name === "SecurityError") return messageFor("VC-MEDIA-001");
    if (e.name === "NotFoundError" || e.name === "OverconstrainedError") return noDevice;
    return messageFor("VC-SYS-001");
  }
  return messageFor("VC-SYS-001");
}

/** What the long-lived session reads from the latest render. */
export interface ReadinessLatest {
  onReady: (prefs: MediaDevicePreferences) => void;
  autoContinue: boolean;
  t: ReturnType<typeof useI18n>["t"];
  messageFor: (code: string) => string;
  cameraEnabled: boolean;
  micEnabled: boolean;
}

/** The component's state setters the session drives. */
export interface ReadinessSetters {
  setLoading: (v: boolean) => void;
  setStreamReady: (v: boolean) => void;
  setHealth: (v: PreviewHealth | null) => void;
  setCameras: (v: DeviceOption[]) => void;
  setMics: (v: DeviceOption[]) => void;
  setCameraId: (v: string | undefined) => void;
  setMicId: (v: string | undefined) => void;
  setError: (v: string | null) => void;
}

/**
 * The exact media/sink/options triple <DeviceReadiness> hands to its ReadinessSession (exported so
 * tests drive the real binding, not a copy): session events → component state, the latest
 * onReady/autoContinue props, toggles applied to each new preview, localized errors.
 */
export function createReadinessBinding(
  set: ReadinessSetters,
  latest: () => ReadinessLatest,
  attach: (stream: MediaStream) => void,
  media: ConstructorParameters<typeof ReadinessSession>[0]
): ConstructorParameters<typeof ReadinessSession> {
  return [
    media,
    {
      acquiring: () => {
        set.setLoading(true);
        set.setStreamReady(false); // re-prove access on every (re)acquire before continue is allowed
        set.setHealth(null);
      },
      stream: (stream) => {
        const vt = stream.getVideoTracks()[0];
        if (vt) vt.enabled = latest().cameraEnabled;
        const at = stream.getAudioTracks()[0];
        if (at) at.enabled = latest().micEnabled;
        attach(stream);
      },
      devices: (cams, ms, nextCam, nextMic) => {
        set.setCameras(cams);
        set.setMics(ms);
        // Default the selection to a concrete device on first load (one extra refresh).
        set.setCameraId(nextCam);
        set.setMicId(nextMic);
      },
      ready: () => {
        set.setError(null);
        set.setStreamReady(true);
        set.setLoading(false);
      },
      failed: (e) => {
        const { t: strings, messageFor } = latest();
        set.setError(toReadinessError(e, strings.readiness.noDevice, strings.readiness.trackEnded, messageFor));
        set.setStreamReady(false);
        set.setLoading(false);
      },
      health: set.setHealth
    },
    {
      autoContinue: () => latest().autoContinue,
      onAutoContinue: (prefs) => latest().onReady(prefs),
      deviceLabel: (kind, i) => `${kind === "videoinput" ? latest().t.readiness.camera : latest().t.readiness.microphone} ${i + 1}`
    }
  ];
}

export function DeviceReadiness({
  role,
  onReady,
  continueLabel: continueLabelOverride,
  autoContinue = false
}: {
  role: "traffic" | "field";
  onReady: (prefs: MediaDevicePreferences) => void;
  /** Overrides the role's continue label (e.g. "Ready" in the public phone flow). */
  continueLabel?: string;
  /**
   * Opt-in (guest invitation flow): no continue button; `onReady` fires once, automatically, as
   * soon as both the camera and the microphone preview tracks are live, enabled and unmuted.
   */
  autoContinue?: boolean;
}) {
  const { t, dir, messageFor } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameras, setCameras] = useState<DeviceOption[]>([]);
  const [mics, setMics] = useState<DeviceOption[]>([]);
  const [cameraId, setCameraId] = useState<string | undefined>(undefined);
  const [micId, setMicId] = useState<string | undefined>(undefined);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // True only after a preview stream has been acquired; gates the continue button so the
  // user cannot proceed before camera/mic access is proven.
  const [streamReady, setStreamReady] = useState(false);
  const [retry, setRetry] = useState(0);
  const [health, setHealth] = useState<PreviewHealth | null>(null);

  // Latest values for the long-lived session (it outlives renders; props/locale may change).
  const latest = useRef<ReadinessLatest>({ onReady, autoContinue, t, messageFor, cameraEnabled, micEnabled });
  latest.current = { onReady, autoContinue, t, messageFor, cameraEnabled, micEnabled };
  const streamRef = useRef<MediaStream | null>(null);

  // One session per mounted readiness stage (lazy state: survives StrictMode's effect replay).
  const [session] = useState(() => new ReadinessSession(...createReadinessBinding(
    { setLoading, setStreamReady, setHealth, setCameras, setMics, setCameraId, setMicId, setError },
    () => latest.current,
    (stream) => {
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    },
    {
      getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c),
      enumerateDevices: () => navigator.mediaDevices.enumerateDevices()
    }
  )));

  // Acquire (or re-acquire) the preview whenever the selected device changes or on retry.
  // Request permission first, THEN enumerate so device labels are populated (browsers hide
  // labels until a grant). Toggles do not re-acquire — they flip track.enabled directly.
  // The session's returned cleanup stops the tracks and ignores late results.
  useEffect(() => {
    const support = evaluateMediaSupport();
    if (support !== "ok") {
      setStreamReady(false);
      setError(messageFor(mediaSupportCode(support)));
      setLoading(false);
      return;
    }
    return session.acquire({ cameraId, micId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, micId, retry]);

  const toggleCamera = () => {
    const next = !cameraEnabled;
    setCameraEnabled(next);
    const vt = streamRef.current?.getVideoTracks()[0];
    if (vt) vt.enabled = next;
    session.reevaluate(); // auto mode: switching a device back on may complete readiness
  };
  const toggleMic = () => {
    const next = !micEnabled;
    setMicEnabled(next);
    const at = streamRef.current?.getAudioTracks()[0];
    if (at) at.enabled = next;
    session.reevaluate();
  };

  const canContinue = canContinueReadiness({ loading, error: !!error, streamReady });

  const onContinue = () => {
    if (!canContinue || autoContinue) return; // guard: never proceed before device access is proven
    session.release();
    onReady({ cameraDeviceId: cameraId, micDeviceId: micId, cameraEnabled, micEnabled });
  };

  const continueLabel = continueLabelOverride ?? (role === "traffic" ? t.readiness.continueTraffic : t.readiness.continueField);
  const autoStatus = health === "healthy" ? t.readiness.autoJoining
    : health === "waiting" ? t.readiness.autoWaiting
      : loading ? t.readiness.requesting
        : t.readiness.autoChecking;

  return (
    <main
      dir={dir}
      style={{
        height: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
        gap: 12, padding: 16, maxWidth: 720, margin: "0 auto", width: "100%", boxSizing: "border-box",
        fontFamily: "Segoe UI, Arial, sans-serif"
      }}
    >
      <div style={{ flex: "0 0 auto" }}>
        <h1 style={{ color: "#14305f", fontSize: 18, margin: "0 0 2px" }}>{t.readiness.title}</h1>
        <p style={{ color: "#5b6b82", fontSize: 13, margin: 0 }}>{t.readiness.subtitle}</p>
      </div>

      {/* Preview fills the remaining height and clips; the controls + button below stay in view. */}
      <div style={{ flex: 1, minHeight: 0, position: "relative", background: "#0f1e3a", borderRadius: 10, overflow: "hidden" }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)", opacity: cameraEnabled ? 1 : 0 }}
        />
        {!cameraEnabled && !error && (
          <div style={overlayCentre}>{t.readiness.cameraOffPreview}</div>
        )}
        {error && (
          <div style={{ ...overlayCentre, flexDirection: "column", gap: 12, padding: 20, textAlign: "center" }}>
            <span style={{ maxWidth: 420 }}>{error}</span>
            <button onClick={() => { setError(null); setRetry((r) => r + 1); }} style={primaryBtn}>
              {t.readiness.retry}
            </button>
          </div>
        )}
        {loading && !error && <div style={{ ...overlayCentre, fontSize: 13 }}>{t.readiness.requesting}</div>}
      </div>

      {/* Device selectors + on/off toggles. Wrap on narrow screens. */}
      <div style={{ flex: "0 0 auto", display: "flex", flexWrap: "wrap", gap: 10 }}>
        <label style={field}>
          <span style={fieldLabel}>{t.readiness.camera}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <select value={cameraId ?? ""} onChange={(e) => setCameraId(e.target.value || undefined)} style={select} disabled={!!error}>
              {cameras.length === 0 && <option value="">—</option>}
              {cameras.map((c) => <option key={c.deviceId} value={c.deviceId}>{c.label}</option>)}
            </select>
            <button
              type="button"
              onClick={toggleCamera}
              style={toggleBtn(cameraEnabled)}
              aria-label={cameraEnabled ? t.readiness.cameraOn : t.readiness.cameraOff}
              aria-pressed={cameraEnabled}
              title={cameraEnabled ? t.readiness.cameraOn : t.readiness.cameraOff}
            >
              <MaterialMediaIcon name={cameraEnabled ? "video_camera_front" : "video_camera_front_off"} />
            </button>
          </div>
        </label>

        <label style={field}>
          <span style={fieldLabel}>{t.readiness.microphone}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <select value={micId ?? ""} onChange={(e) => setMicId(e.target.value || undefined)} style={select} disabled={!!error}>
              {mics.length === 0 && <option value="">—</option>}
              {mics.map((m) => <option key={m.deviceId} value={m.deviceId}>{m.label}</option>)}
            </select>
            <button
              type="button"
              onClick={toggleMic}
              style={toggleBtn(micEnabled)}
              aria-label={micEnabled ? t.readiness.micOn : t.readiness.micOff}
              aria-pressed={micEnabled}
              title={micEnabled ? t.readiness.micOn : t.readiness.micOff}
            >
              <MaterialMediaIcon name={micEnabled ? "mic" : "mic_off"} />
            </button>
          </div>
        </label>
      </div>

      {autoContinue ? (
        // No continue button: the stage proceeds by itself once both devices work. Problems are
        // shown on the preview (with "Try again"); a muted/switched-off device can be retried here.
        <div data-readiness="auto" role="status" aria-live="polite"
          style={{ flex: "0 0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, color: "#14305f", fontSize: 14, minHeight: 44 }}>
          {!error && <span>{autoStatus}</span>}
          {!error && health === "waiting" && (
            <button type="button" onClick={() => setRetry((r) => r + 1)} style={{ ...primaryBtn, background: "#1d4ed8", padding: "8px 16px", fontSize: 14 }}>
              {t.readiness.retry}
            </button>
          )}
        </div>
      ) : (
        <button
          data-readiness="continue"
          onClick={onContinue}
          disabled={!canContinue}
          style={{ ...primaryBtn, flex: "0 0 auto", opacity: canContinue ? 1 : 0.5, cursor: canContinue ? "pointer" : "not-allowed" }}
        >
          {loading && !error ? t.readiness.requesting : continueLabel}
        </button>
      )}
    </main>
  );
}

const overlayCentre: React.CSSProperties = {
  position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
  color: "#dbe7ff", fontSize: 15, background: "rgba(15,30,58,0.35)"
};
const field: React.CSSProperties = { flex: "1 1 260px", minWidth: 220, display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel: React.CSSProperties = { fontSize: 12, color: "#64748b" };
const select: React.CSSProperties = { flex: 1, minWidth: 0, padding: "8px 10px", border: "1px solid #d6deea", borderRadius: 6, fontSize: 14 };
const primaryBtn: React.CSSProperties = {
  background: "#15803d", color: "#fff", border: "none", borderRadius: 8, padding: "12px 22px", fontSize: 15, cursor: "pointer"
};
function toggleBtn(on: boolean): React.CSSProperties {
  return {
    background: on ? "#1d4ed8" : "#e2e8f0", color: on ? "#fff" : "#1e2a3a",
    border: "none", borderRadius: 6, width: 44, minWidth: 44, height: 38, padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, cursor: "pointer", whiteSpace: "nowrap"
  };
}
